import { useAccountingStore } from "../../store/Accounting";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDateRangePicker } from "../../components/custom-date-range-picker/custom-date-range-picker.component";
import { AbstractInvoiceTable } from "./components/abstract-invoice-table.component";
import { TableExportDialog } from "./components/table-export-dialog.component";
import { AccountingRecipientSelector } from "./components/accounting-recipient-selector.component";
import {
	ExportData,
	ExportDataResult,
	ExportFormat,
	TableExportOptions,
	useTableExport,
} from "../../hooks/useTableExport";
import {
	EXPORT_MAX_ROWS,
	ExportHeaderMetadata,
} from "../../lib/utils/export-utils";
import { SortingState, PaginationState } from "@tanstack/react-table";
import { Search, Info, X } from "lucide-react";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import { useCentralStore } from "../../store/Central";
import { Logger } from "@/lib/logger/Logger";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import { TableRowData } from "./types/invoice-table.types";
import {
	PaymentJournalRowWithSubRows,
	InvoiceAllocationSubRow,
	InvoiceAllocationDetail,
} from "./types/payment-journal.types";

export const ZahlungsjournalPage: React.FC = () => {
	const organization = useCentralStore((state) => state.organization);
	const [loading, setLoading] = useState<boolean>(false);
	const [dateValue, setDateValue] = useState<SelectionRange>({
		start: null,
		end: null,
	});
	const [selectedRecipientId, setSelectedRecipientId] = useState<string>("");

	const [sorting, setSorting] = useState<SortingState>([
		{ id: "valuta_date", desc: true },
	]);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 100,
	});
	const [globalFilter, setGlobalFilter] = useState<string>("");
	const [totalRowCount, setTotalRowCount] = useState<number>(0);
	const requestIdRef = useRef(0); //necessary to prevent race conditions when fetching data

	const { paymentJournalData, fetchPaymentJournalData, recipientsLookup } =
		useAccountingStore((state) => ({
			paymentJournalData: state.paymentJournalData,
			fetchPaymentJournalData: state.fetchPaymentJournalData,
			recipientsLookup: state.recipientsLookup,
		}));

	const { exportTable } = useTableExport();

	const formatDate = (date: Date | null): string => {
		if (!date) return "";
		return date.toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const headerMetadata: ExportHeaderMetadata = useMemo(() => {
		const recipientName = selectedRecipientId
			? recipientsLookup[selectedRecipientId]?.name
			: undefined;

		const sortingLabel = sorting[0]
			? `${
					sorting[0].id === "valuta_date"
						? "Valutadatum"
						: sorting[0].id
				} (${sorting[0].desc ? "absteigend" : "aufsteigend"})`
			: undefined;

		return {
			exportType: "zahlungsjournal" as const,
			exportTypeLabel: "Zahlungsjournal",
			exportDate: formatDate(new Date()),
			dateRange:
				dateValue.start && dateValue.end
					? {
							start: formatDate(dateValue.start),
							end: formatDate(dateValue.end),
						}
					: undefined,
			recipientFilter: recipientName,
			globalFilter: globalFilter || undefined,
			sorting: sortingLabel,
		};
	}, [
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		recipientsLookup,
		sorting,
		globalFilter,
	]);

	const fetchData = useCallback(async () => {
		const organizationId = organization?.id;
		if (!organizationId) {
			Logger.error("No organization id found");
			return;
		}

		const currentRequestId = ++requestIdRef.current;
		setLoading(true);

		try {
			const sortBy = sorting[0]?.id || "valuta_date";
			const sortDirection = sorting[0]?.desc ? "desc" : "asc";

			const result = await fetchPaymentJournalData(
				dateValue.start ? formatLocalDate(dateValue.start) : "",
				dateValue.end ? formatLocalDate(dateValue.end) : "",
				selectedRecipientId,
				{
					sortBy,
					sortDirection,
					pageIndex: pagination.pageIndex,
					pageSize: pagination.pageSize,
					globalFilter: globalFilter,
					updateStore: true,
				}
			);

			if (requestIdRef.current === currentRequestId) {
				if (result) {
					setTotalRowCount(result.totalCount);
				} else {
					setTotalRowCount(0);
				}
			}
		} finally {
			if (requestIdRef.current === currentRequestId) {
				setLoading(false);
			}
		}
	}, [
		organization?.id,
		fetchPaymentJournalData,
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		sorting,
		pagination,
		globalFilter,
	]);

	const fetchExportData = async (): Promise<ExportDataResult> => {
		const currentSortBy = sorting[0]?.id || "valuta_date";
		const currentSortDirection = sorting[0]?.desc ? "desc" : "asc";

		return fetchPaymentJournalData(
			dateValue.start ? formatLocalDate(dateValue.start) : "",
			dateValue.end ? formatLocalDate(dateValue.end) : "",
			selectedRecipientId,
			{
				sortBy: currentSortBy,
				sortDirection: currentSortDirection,
				pageIndex: 0,
				pageSize: EXPORT_MAX_ROWS,
				globalFilter,
				updateStore: false,
			}
		);
	};

	const handleExport = (
		exportData: ExportData,
		format: ExportFormat,
		options: TableExportOptions,
		metadata: ExportHeaderMetadata
	) =>
		exportTable(
			exportData,
			format,
			{
				...options,
				includePayments: true, // Always include payments in Zahlungsjournal
				includeVAT: false,
			},
			metadata
		);

	useEffect(() => {
		const isDateRangeValid =
			(dateValue.start && dateValue.end) ||
			(!dateValue.start && !dateValue.end);
		if (!isDateRangeValid) return;
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateValue, selectedRecipientId, sorting, pagination, globalFilter]);

	// Handle sorting changes - reset to page 1 when sorting changes
	const handleSortingChange = (
		updater: SortingState | ((old: SortingState) => SortingState)
	) => {
		setSorting(updater);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	return (
		<div className="py-6 flex flex-col w-full items-center">
			<div className="w-full max-w-[1920px] px-6">
				{/* Toolbar */}
				<div className="flex items-center justify-between gap-4 py-3 mb-4 border-b border-gray-200">
					{/* Left: Filters */}
					<div className="flex items-center gap-6">
						<TooltipWrapper title="Die Ansicht zeigt alle gültigen Zahlungseingänge inkl. Zuordnung zu Rechnungen.">
							<button
								type="button"
								className="text-gray-400 hover:text-gray-600 p-1"
								aria-label="Zweck anzeigen"
							>
								<Info className="h-4 w-4" />
							</button>
						</TooltipWrapper>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 whitespace-nowrap">
								Zeitraum:
							</span>
							<CustomDateRangePicker
								dateVal={dateValue}
								handleDateChange={setDateValue}
							/>
							{(dateValue.start || dateValue.end) && (
								<button
									type="button"
									onClick={() =>
										setDateValue({ start: null, end: null })
									}
									className="text-gray-400 hover:text-gray-600"
									aria-label="Zeitraum löschen"
								>
									<X size={16} />
								</button>
							)}
						</div>
						<div className="h-8 w-px bg-gray-200" />
						<AccountingRecipientSelector
							selectedRecipientId={selectedRecipientId}
							onRecipientChange={setSelectedRecipientId}
							recipientList={recipientsLookup}
						/>
						<div className="h-8 w-px bg-gray-200" />
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Suchen..."
								value={globalFilter}
								onChange={(e) =>
									setGlobalFilter(e.target.value)
								}
								className="w-64 h-10 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
						</div>
					</div>
					{/* Right: Actions */}
					<div className="flex items-center gap-2">
						<TableExportDialog
							invoiceData={paymentJournalData}
							loading={loading}
							incompleteDateRange={
								Boolean(dateValue.start) !==
								Boolean(dateValue.end)
							}
							onExport={handleExport}
							fetchExportData={fetchExportData}
							currentSorting={
								sorting[0]
									? `${sorting[0].id} (${
											sorting[0].desc
												? "absteigend"
												: "aufsteigend"
										})`
									: "Valutadatum (absteigend)"
							}
							headerMetadata={headerMetadata}
							showInvoicesOption={true}
						/>
					</div>
				</div>

				<div className="flex justify-center">
					<AbstractInvoiceTable<TableRowData>
						data={(() => {
							if (
								!paymentJournalData ||
								!Array.isArray(paymentJournalData)
							) {
								return [];
							}
							return paymentJournalData
								.filter((payment) => payment != null)
								.map(
									(
										payment
									): PaymentJournalRowWithSubRows => ({
										...payment,
										isInvoiceSubRow: false as const,
										subRows: (
											payment.invoice_allocations as
												| InvoiceAllocationDetail[]
												| null
										)?.map(
											(
												allocation
											): InvoiceAllocationSubRow => ({
												...allocation,
												payment_id: payment.payment_id,
												isInvoiceSubRow: true as const,
											})
										),
									})
								) as unknown as TableRowData[];
						})()}
						tableType="payment-journal"
						loading={loading}
						highlightedRows={[]}
						features={undefined}
						uiCallbacks={{
							onRefreshData: fetchData,
						}}
						sorting={sorting}
						onSortingChange={handleSortingChange}
						pagination={pagination}
						onPaginationChange={setPagination}
						globalFilter={globalFilter}
						onGlobalFilterChange={setGlobalFilter}
						totalRowCount={totalRowCount}
						manualSorting={true}
						manualPagination={true}
						manualFiltering={true}
						showVATSummary={false}
						showPaymentSummary={false}
						emptyStateLabel="Keine Zahlungen vorhanden"
					/>
				</div>
			</div>
		</div>
	);
};
